import { computed, toValue } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { resolveActiveLabels } from 'dashboard/helper/labelHelper';

/**
 * Composable for managing contact labels in the conversation sidebar.
 * @param {import('vue').MaybeRefOrGetter<number|string|null>} contactId
 */
export function useContactLabels(contactId) {
  const store = useStore();
  const getters = useStoreGetters();

  const resolvedContactId = computed(() => toValue(contactId));

  const accountLabels = computed(() => getters['labels/getLabels'].value);

  const savedLabels = computed(() => {
    const id = resolvedContactId.value;
    if (!id) return [];
    return store.getters['contactLabels/getContactLabels'](id);
  });

  const pendingLabelTitles = computed(() => {
    const id = resolvedContactId.value;
    if (!id) return null;
    return store.getters['contactLabels/getPendingLabels'](id);
  });

  const updatingLabelTitles = computed(() => {
    const id = resolvedContactId.value;
    if (!id) return [];
    return store.getters['contactLabels/getUpdatingLabels'](id);
  });

  const visibleLabelTitles = computed(
    () => pendingLabelTitles.value ?? savedLabels.value
  );

  const activeLabels = computed(() =>
    resolveActiveLabels(visibleLabelTitles.value, accountLabels.value)
  );

  const selectedLabelTitles = computed(() =>
    activeLabels.value.map(label => label.title)
  );

  const onUpdateLabels = async selectedLabels => {
    const id = resolvedContactId.value;
    if (!id) return;
    await store.dispatch('contactLabels/update', {
      contactId: id,
      labels: selectedLabels,
    });
  };

  const addLabelToContact = value => {
    const title = value?.title;
    if (!title || updatingLabelTitles.value.includes(title)) return;

    const result = [...visibleLabelTitles.value];
    if (!result.includes(title)) result.push(title);
    onUpdateLabels(result);
  };

  const removeLabelFromContact = value => {
    if (updatingLabelTitles.value.includes(value)) return;

    const result = visibleLabelTitles.value.filter(label => label !== value);
    onUpdateLabels(result);
  };

  const fetchContactLabels = () => {
    const id = resolvedContactId.value;
    if (!id) return;
    store.dispatch('contactLabels/get', id);
  };

  return {
    accountLabels,
    savedLabels,
    activeLabels,
    selectedLabelTitles,
    updatingLabelTitles,
    addLabelToContact,
    removeLabelFromContact,
    fetchContactLabels,
  };
}
